import { useEffect, useState } from 'react'
import { usePollen } from '@pilot-hub/ui'
import { Marker, Polygon, Polyline, Popup, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import { api } from '@pilot-hub/api-client'
import { MarkierungInfo } from '@pilot-hub/ui/components/karte/index'


// Ebene „Markierungen Pilot 2“: Punkte, Linien und Flächen, die der Pilot in DJI Pilot 2 auf dem Controller
// zeichnet (Map-Modul der Cloud API, gespeichert von der Pilot-API in dji_kartenelemente). Nur Anzeige.

export interface PilotMarkierung {
  id: string
  name: string | null
  typ: number
  inhalt: { properties?: { color?: string }; geometry?: { type?: string; coordinates?: any } }
  ersteller: string | null
  erstellt_am: string
}

/** Markierungen des Einsatzes, alle 10 s neu */
export function usePilotMarkierungen(einsatzId: string | number | null | undefined) {
  const [markierungen, setMarkierungen] = useState<PilotMarkierung[]>([])
  useEffect(() => { setMarkierungen([]) }, [einsatzId])
  usePollen('Pilot-Markierungen', lebt => api.get('/dji/kartenelemente', { params: einsatzId ? { einsatz_id: einsatzId } : {} })
    .then(r => { if (lebt()) setMarkierungen(r.data as PilotMarkierung[]) }), 10000, [einsatzId])
  return markierungen
}

// DJI liefert Farben als „#RRGGBB“ oder „0xRRGGBB“
const farbe = (m: PilotMarkierung) => {
  const c = m.inhalt?.properties?.color || '#E23C39'
  return c.startsWith('0x') ? `#${c.slice(2)}` : c
}

const pinIcon = (f: string) => L.divIcon({
  className: '', iconSize: [24, 32], iconAnchor: [12, 30],
  html: `<svg width="24" height="32" viewBox="0 0 24 32"><path d="M12 31s10-11.2 10-18.5A10 10 0 0 0 2 12.5C2 19.8 12 31 12 31z" fill="${f}" stroke="#fff" stroke-width="2"/><circle cx="12" cy="12.5" r="3.5" fill="#fff"/></svg>`,
})

const uhr = (s: string) => new Date(s).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })

function Info({ m }: { m: PilotMarkierung }) {
  return (
    <>
      <Tooltip sticky><span className="text-xs"><b>{m.name || 'Markierung'}</b> · Pilot 2{m.ersteller ? ` (${m.ersteller})` : ''} · {uhr(m.erstellt_am)}</span></Tooltip>
      <Popup minWidth={240}><MarkierungInfo name={m.name} zeit={m.erstellt_am} quelle={`DJI Pilot 2${m.ersteller ? ` (${m.ersteller})` : ''}`} geometrie={m.inhalt?.geometry} /></Popup>
    </>
  )
}

/** Kartenebene (innerhalb von MapContainer). GeoJSON hat [lon, lat] – Leaflet braucht [lat, lon]. */
export function PilotMarkierungenLayer({ markierungen }: { markierungen: PilotMarkierung[] }) {
  return (
    <>
      {markierungen.map(m => {
        const g = m.inhalt?.geometry
        const f = farbe(m)
        const ll = (p: number[]) => [p[1], p[0]] as [number, number]
        if (g?.type === 'Point' && Array.isArray(g.coordinates)) {
          return <Marker key={m.id} position={ll(g.coordinates)} icon={pinIcon(f)}><Info m={m} /></Marker>
        }
        if (g?.type === 'LineString' && Array.isArray(g.coordinates)) {
          return <Polyline key={m.id} positions={g.coordinates.map(ll)} pathOptions={{ color: f, weight: 4 }}><Info m={m} /></Polyline>
        }
        if (g?.type === 'Polygon' && Array.isArray(g.coordinates?.[0])) {
          return <Polygon key={m.id} positions={g.coordinates[0].map(ll)} pathOptions={{ color: f, weight: 2, fillOpacity: 0.15 }}><Info m={m} /></Polygon>
        }
        return null
      })}
    </>
  )
}
